import React, { useEffect, useState } from 'react'
import { Button, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { track } from 'shared/util/analytics'
import { useSettingsOutdated } from 'src/hooks/useSettingsOutdated'
import pageMargins from '../styles/snippets/pageMargins'

const useStyles = makeStyles<Theme, SettingsOutdatedBannerProps>(
  (theme) => ({
    root: {
      minWidth: '100%',
      display: 'block',
      backgroundColor: theme.palette.warning.light
    },
    alert: {
      ...pageMargins(theme, ''),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      color: theme.palette.warning.dark,
      padding: `${theme.spacing(1.5)}px 0`
    },
    alertContent: {
      display: 'flex',
      alignItems: 'center'
    },
    icon: {
      marginRight: theme.spacing(1)
    },
    heading: {
      fontWeight: 600,
      marginRight: theme.spacing(0.5)
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    dismissButton: {
      color: 'inherit'
    }
  }),
  { name: 'SettingsOutdatedBanner' }
)

export interface SettingsOutdatedBannerProps {
  classes?: StyleClasses<typeof useStyles>
}

/**
 * Tells the user that another session changed the company settings, and offers a reload (#25436).
 * Dismissing hides it for that change only; a newer change shows it again.
 */
function SettingsOutdatedBanner(props: SettingsOutdatedBannerProps) {
  const classes = useStyles(props)
  const { outdated, versionKey } = useSettingsOutdated()
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  const show = outdated && versionKey !== dismissedKey
  // The server version on screen, or null while hidden. Tracking on this key logs one event per
  // distinct version shown, not one per render.
  const shownKey = show ? versionKey : null

  useEffect(() => {
    if (shownKey) {
      track('stale_settings_notice_shown')
    }
  }, [shownKey])

  if (!show) {
    return null
  }

  return (
    <div className={classes.root}>
      <div className={classes.alert}>
        <div className={classes.alertContent}>
          <InfoOutlinedIcon className={classes.icon} />
          <Typography variant="body1">
            <span className={classes.heading}>Account settings were changed in another session.</span>
            Reload to see the latest.
          </Typography>
        </div>
        <div className={classes.actions}>
          <Button variant="text" className={classes.dismissButton} onClick={() => setDismissedKey(versionKey)}>
            Later
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              track('stale_settings_refresh_clicked')
              window.location.reload()
            }}
          >
            Reload
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SettingsOutdatedBanner
